---
title: "Why a browser voice recorder saves WebM instead of MP3"
description: "Learn why a browser recorder may download WebM, OGG, or MP4 instead of MP3, how to identify the real format, and when conversion is required."
locale: "en"
date: "2026-08-03"
tags: ["webm", "mp3", "voice recording", "mediarecorder"]
relatedTools: ["voice-recorder"]
translations:
  ru: "pochemu-diktofon-sohranyaet-webm-a-ne-mp3"
faq:
  - q: "Why did my recording download as WebM?"
    a: "The .webm extension is used for known WebM options and for an unrecognised browser-default format. Check the actual MIME type displayed in the interface; it describes the result more accurately than the filename."
  - q: "Can I rename .webm to .mp3?"
    a: "No. Renaming changes only the filename, not the container or codec. An app that requires real MP3 audio may still reject the file."
  - q: "Does mediatik upload the recording to convert it?"
    a: "No. The current version records and previews audio locally in the browser. It does not include MP3 conversion."
---

If your voice recording downloads as `mediatik-voice.webm`, do not identify its
format from the extension alone. The [online voice recorder](/diktofon-online)
shows the actual MIME type reported by `MediaRecorder`, and that value is the
one to check. The current download helper also uses `.webm` for an unrecognised
browser-default MIME type. What remains certain is that renaming the file
cannot turn it into MP3.

## The short answer

Mediatik uses the browser's `MediaRecorder` API. Before recording, it checks
WebM with Opus, OGG with Opus, audio in an MP4 container, and WebM without an
explicit codec, in that order. It selects the first type that the browser
reports as supported.

If the browser reports none of those candidates, mediatik lets the browser use
its default. After recording, the app reads the recorder's actual `mimeType`
and shows the format. For the known containers in its candidate list, mediatik
uses a matching `.webm`, `.ogg`, or `.mp4` extension.

That means one browser may produce `.webm`, while another may produce `.ogg`
or `.mp4`. The current mediatik recorder does not promise one container across
all devices, and it does not advertise MP3 export.

## Why WebM can be an audio file

WebM is a container, not another word for video. A WebM file can carry only an
audio track, commonly compressed with a codec such as Opus. A voice recording
named `mediatik-voice.webm` therefore does not need to contain a video track.

A container organises data in a file, such as WebM, OGG, or MP4. A codec such
as Opus compresses the audio stream. A filename extension helps software guess
the container but does not change the data inside it.

A MIME type such as `audio/webm;codecs=opus` describes both the WebM container
and the Opus audio codec. It is more precise than the `.webm` extension alone.

## How the browser selects a format

[`MediaRecorder.isTypeSupported()` on MDN](https://developer.mozilla.org/en-US/docs/Web/API/MediaRecorder/isTypeSupported_static)
asks whether the browser should be able to record a given MIME type. A `true`
result reports expected support; it cannot guarantee success when resources
are insufficient or the input device fails.

[`MediaRecorder.mimeType` on MDN](https://developer.mozilla.org/en-US/docs/Web/API/MediaRecorder/mimeType)
reports the MIME type selected by the app or browser for the resulting file.
A mismatched container and codec combination may fail to record or may produce
a file that players cannot handle correctly.

Mediatik therefore does not put an `.mp3` name on a Blob whose actual contents
are WebM. That label might look more convenient, but it would leave the encoded
data unchanged and mislead the receiving app.

## What to do when a service requires MP3

First confirm whether the destination truly requires MP3. Some upload forms can
play WebM or MP4 even when their short hint does not list every accepted type.
If the form explicitly rejects the recording:

1. Download the original from mediatik without renaming it.
2. Note the format shown beside the recording.
3. Convert a copy with a tool that actually decodes and re-encodes audio as MP3.
4. Play the MP3 before uploading it; check the beginning, end, and volume.
5. Keep the original until the upload or editing job is complete.

Changing `.webm` to `.mp3` by hand is not conversion. The filename changes,
but the original container and codec selected by the browser do not. Format
validation may therefore still fail.

## What mediatik does with the recording

Microphone access is requested through the browser's permission prompt. Audio
chunks are collected in the open tab, then combined into a local Blob for
preview and download when recording stops. The recording is not uploaded and
there is no hidden server-side conversion step.

Local processing does not imply MP3 output. Encoding MP3 would require a
separate codec and another processing stage. Mediatik V1 deliberately omits
that stage: it saves the actual `MediaRecorder` result and displays its MIME
type.

## Check the file before using it

Note the displayed MIME type and play the recording from beginning to end.
Compare the destination's accepted containers and codecs. If MP3 is mandatory,
perform a real conversion instead of a rename, and keep the original until the
upload or edit is complete.

For a recording that stays in the browser, open the
[mediatik voice recorder](/diktofon-online), allow microphone access, and check
the browser-selected format before downloading the result.
